import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSelector,
  createSlice} from '@reduxjs/toolkit'

import type { ILandingPageFontSummaryDto } from 'services/repositories/LandingPageFontsRepository'
import LandingPagesRepository, {
  IAddAssetToSectionRequest,
  IAddSectionRequest,
  IConfigAvailableSectionType,
  ICreateAssetTypeRequest,
  ICreateConfigRequest,
  ICreateLandingPageRequest,
  IGenerateLandingPagePackageRequest,
  IGenerateLandingPagePackagesRequest,
  ILandingPageQuery,
  ILandingPageTemplateBootstrap,
  ILandingPageTemplateSummary,
  ILandingPageVariantsListResponse,
  IReorderSectionsRequest,
  IUpdateAssetTypeDto,
  IUpdateConfigDto,
  IUpdateLandingPageDto,
  IUpdateSectionRequest} from 'services/repositories/LandingPagesRepository'
import { IPagination } from 'types/assets'
import { LoadingStatus, TLoadingStatus } from 'types/user'
import { handleApiErrors } from 'utils/errors'
import { normalizeLandingPageVariantsListResponse } from 'utils/landingPageVariantDisplay'

/** Landing page – matches LANDING_PAGES_FRONTEND_IMPLEMENTATION Data Models */
export interface ILandingPage {
  id: string
  name: string
  brandId: string
  productCategoryIds: string[] // Array of ProductCategory IDs (many-to-many)
  notes?: string | null
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null
  isMultipleProduct: boolean // Flag for single vs multiple product landing pages
  country: string
  landingPageConfigId?: string
  sections?: ILandingPageSection[] // Optional: loaded when needed
  productCategories: { id: string; name: string }[] // Array of ProductCategory objects
  productCategoryNames?: string // Comma-separated (API serialized)
  brand: { id: string; name: string }
  brandName?: string
  templateLanding?: boolean
  templateName?: string | null
  templateDescription?: string | null
  templatePreviewUrl?: string | null
  templatePreviewMimeType?: string | null
  pageBackgroundDesktopUrl?: string | null
  pageBackgroundDesktopMimeType?: string | null
  pageBackgroundMobileUrl?: string | null
  pageBackgroundMobileMimeType?: string | null
  allProductCategories?: boolean
  /** True for template LPs when the caller cannot manage templates */
  isReadOnly?: boolean
  /** Selected LP font (active, brand-allowed); use for editor dropdown */
  selectedFontId?: string | null
  selectedFont?: { id: string; fontFamily: string } | null
  config?: any
  createdBy: string | { id: string }
  updatedBy?: string | { id: string }
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

/** Section – matches LANDING_PAGES_FRONTEND_IMPLEMENTATION Data Models */
export interface ILandingPageSection {
  id: string
  landingPageId: string
  landingPageAssetTypeId?: string // Optional; API may omit when embedding landingPageAssetType
  /** Config section type id (from availableSectionTypes[].id); API returns it so client can send on update. */
  sectionTypeId?: string | null
  orderInPage: number
  selectedVariant: string
  landingPageAssetType?: {
    id: string
    name: string
    brand?: { id: string; name: string }
    maxNumberPerSection?: number
  }
  sectionAssets?: ILandingPageSectionAsset[]
  /** Include this section in the page navigation (max 12 per LP). */
  inNavigation?: boolean
  /** Used in href="#anchorId" when inNavigation is true. */
  anchorId?: string | null
  /** Optional label for the nav link. */
  navLabel?: string | null
  /** Lead LP asset (type "Lead") for section title/heading, rendered above section content. */
  leadLandingPageAssetId?: string | null
  /** @deprecated Lead variant is derived from section variant; present on GET only. */
  selectedLeadVariant?: string | null
  leadLandingPageAsset?: ILandingPageAsset
  accentColor?: string | null
  backgroundColor?: string | null
  fontColor?: string | null
  secondaryFontColor?: string | null

  createdAt: string
  updatedAt: string
}

/** Section asset – matches LANDING_PAGES_FRONTEND_IMPLEMENTATION Data Models */
export interface ILandingPageSectionAsset {
  id: string
  landingPageSectionId: string
  landingPageAssetId: string
  orderInSection: number
  selectedVariant: string
  assetGroupId?: string | null
  landingPageAsset?: ILandingPageAsset // Full asset object when embedded
  createdAt: string
  updatedAt: string
}

export interface ILandingPageAssetType {
  id: string
  name: string
  brandId: string
  maxNumberPerSection?: number
  brand: { id: string; name: string }
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface ILandingPageAsset {
  // Core identifiers
  id: string
  name: string
  isActive: boolean
  isDraft: boolean

  imageUrl?: string
  imageFileName?: string
  imageBucketKey?: string
  sourceGraphicFileUrl?: string
  sourceGraphicFileFileName?: string
  sourceGraphicFileBucketKey?: string
  largeImageUrl?: string
  largeImageFileName?: string
  largeImageBucketKey?: string
  landingPageFileUrl?: string
  landingPageFileFileName?: string
  landingPageFileBucketKey?: string

  activeImageUrl?: string
  activeImageFileName?: string
  activeImageAlt?: string

  // Text content
  additionalText?: string
  attributeAlt?: string
  attributeAltMobile?: string
  defaultDescription?: string
  descriptionForEuro?: string
  descriptionForTIM?: string

  /** Text-banner copy (LP Banner / text-banner layout); not defaultDescription / descriptionForEuro / descriptionForTIM */
  bannerTitle?: string
  bannerTitleAccent?: string
  bannerSubtitle?: string
  bannerDisclaimer?: string

  // Scoping
  country: string // "PL" | "UA" | etc.

  brandId?: string
  productCategoryId?: string
  landingPageAssetTypeId?: string

  landingPageAssetType?: {
    id: string
    name: string
    brand?: { id: string; name: string }
    maxNumberPerSection?: number
    createdAt?: string
    updatedAt?: string
    deletedAt?: string | null
  }

  landingPageAssetSubtype?: {
    id: string
    name: string
  } | null

  brand?: { id: string; name: string }
  productCategory?: { id: string; name: string }

  // Denormalized fields (for convenience)
  landingPageAssetTypeName?: string
  brandName?: string
  productCategoryName?: string

  // Hotpoints (for asset type Hotpoints)
  hotpointsUrl?: string | null
  hotpointsFileName?: string | null
  hotpointsAlt?: string | null
  hotpointsProperties?: string | null
  hotpointsMobileUrl?: string | null
  hotpointsMobileFileName?: string | null
  hotpointsMobileAlt?: string | null
  hotpointsMobileProperties?: string | null
  /** Haier hotpoints layout (e.g. Haier2). Optional on legacy assets. */
  hotpointTemplate?: string | null

  // Download Button (for asset type Download Button)
  pdfFileUrl?: string | null
  pdfFileName?: string | null

  // Video (for asset type Video – YouTube)
  videoUrl?: string | null
  videoTitle?: string | null
  videoYoutubeId?: string | null

  // Packshot (for asset type Packshot)
  packshotUrl?: string | null
  packshotFileName?: string | null
  packshotAlt?: string | null
  openedPackshotUrl?: string | null
  openedPackshotFileName?: string | null
  openedPackshotAlt?: string | null
  sku?: string | null

  templateAsset?: boolean

  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface ILandingPagesState {
  status: TLoadingStatus
  /** LP list table fetch only — not conflated with edit / asset flows. */
  landingPagesListStatus: TLoadingStatus
  landingPages: ILandingPage[]
  currentLandingPage: ILandingPage | null
  config: any | null
  availableAssets: Record<string, any[]> // keyed by assetTypeId
  availableAssetTypes: ILandingPageAssetType[]
  landingPageAssets: ILandingPageAsset[]
  landingPageAssetsPagination: IPagination
  pagination: IPagination
  /** Admin: asset types list for selected brand */
  adminAssetTypes: ILandingPageAssetType[]
  adminAssetTypesMeta: IPagination
  /** Brand id for cached `adminAssetTypes`. */
  adminAssetTypesBrandId: string | null
  /** Variants list (brand+partner) for preview images – used in admin and add/edit */
  variantsList: ILandingPageVariantsListResponse | null
  /** LP config fetch for add/edit — separate from global save status. */
  configLoading: boolean
  /** Variants-with-previews fetch for add/edit section pickers. */
  variantsListLoading: boolean
  /** Brand slug last loaded into `variantsList` (skip refetch + keep stale UI while refreshing). */
  variantsListBrandSlug: string | null
  /** Brand id for cached `config` (skip redundant config fetch). */
  configBrandId: string | null
  /** Active fonts for LP editor dropdown (per brand). */
  brandFonts: ILandingPageFontSummaryDto[] | null
  brandFontsBrandId: string | null
  brandFontsLoading: boolean
  error: string | null
  landingPageAssetsListPendingCount: number
}

const initialState: ILandingPagesState = {
  status: LoadingStatus.initial,
  landingPagesListStatus: LoadingStatus.initial,
  landingPages: [],
  currentLandingPage: null,
  config: null,
  availableAssets: {},
  availableAssetTypes: [],
  landingPageAssets: [],
  landingPageAssetsPagination: {
    page: 0,
    pagesTotal: 0,
    recordsTotal: 0,
    take: 0
  },
  pagination: {
    page: 0,
    pagesTotal: 0,
    recordsTotal: 0,
    take: 0
  },
  adminAssetTypes: [],
  adminAssetTypesMeta: {
    page: 0,
    pagesTotal: 0,
    recordsTotal: 0,
    take: 0
  },
  adminAssetTypesBrandId: null,
  variantsList: null,
  configLoading: false,
  variantsListLoading: false,
  variantsListBrandSlug: null,
  configBrandId: null,
  brandFonts: null,
  brandFontsBrandId: null,
  brandFontsLoading: false,
  error: null,
  landingPageAssetsListPendingCount: 0
}

export const types = {
  getLandingPages: 'landingPages/getLandingPages',
  getLandingPageById: 'landingPages/getLandingPageById',
  createLandingPage: 'landingPages/createLandingPage',
  updateLandingPage: 'landingPages/updateLandingPage',
  deleteLandingPage: 'landingPages/deleteLandingPage',
  getConfigByBrandId: 'landingPages/getConfigByBrandId',
  getLandingPageFontsForBrand: 'landingPages/getLandingPageFontsForBrand',
  getAvailableAssets: 'landingPages/getAvailableAssets',
  addSection: 'landingPages/addSection',
  updateSection: 'landingPages/updateSection',
  deleteSection: 'landingPages/deleteSection',
  reorderSections: 'landingPages/reorderSections',
  addAssetToSection: 'landingPages/addAssetToSection',
  removeAssetFromSection: 'landingPages/removeAssetFromSection',
  generatePackage: 'landingPages/generatePackage',
  generatePackages: 'landingPages/generatePackages',
  generateIframe: 'landingPages/generateIframe',
  getPackages: 'landingPages/getPackages',
  getPreview: 'landingPages/getPreview',
  getAvailableAssetTypes: 'landingPages/getAvailableAssetTypes',
  listLandingPageAssets: 'landingPages/listLandingPageAssets',
  getLandingPageAssetById: 'landingPages/getLandingPageAssetById',
  createLandingPageAsset: 'landingPages/createLandingPageAsset',
  updateLandingPageAsset: 'landingPages/updateLandingPageAsset',
  updateLandingPageAssetWithJson: 'landingPages/updateLandingPageAssetWithJson',
  deleteLandingPageAsset: 'landingPages/deleteLandingPageAsset',
  copyLandingPageAsset: 'landingPages/copyLandingPageAsset',
  copyFromProductAsset: 'landingPages/copyFromProductAsset',
  copyHotpointsFromProduct: 'landingPages/copyHotpointsFromProduct',
  createConfig: 'landingPages/createConfig',
  updateConfig: 'landingPages/updateConfig',
  deleteConfig: 'landingPages/deleteConfig',
  addSectionTypeToConfig: 'landingPages/addSectionTypeToConfig',
  removeSectionTypeFromConfig: 'landingPages/removeSectionTypeFromConfig',
  getLandingPageAssetTypes: 'landingPages/getLandingPageAssetTypes',
  createLandingPageAssetType: 'landingPages/createLandingPageAssetType',
  updateLandingPageAssetType: 'landingPages/updateLandingPageAssetType',
  deleteLandingPageAssetType: 'landingPages/deleteLandingPageAssetType',
  clearCurrentLandingPage: 'landingPages/clearCurrentLandingPage',
  clearError: 'landingPages/clearError',
  getLandingPageVariantsList: 'landingPages/getLandingPageVariantsList',
  getLandingPageVariantsWithPreviews:
    'landingPages/getLandingPageVariantsWithPreviews',
  uploadVariantPreviewImage: 'landingPages/uploadVariantPreviewImage',
  deleteVariantPreviewImage: 'landingPages/deleteVariantPreviewImage',
  updateVariantLabel: 'landingPages/updateVariantLabel',
  listLandingPageTemplates: 'landingPages/listLandingPageTemplates',
  bootstrapLandingPageTemplate: 'landingPages/bootstrapLandingPageTemplate',
  copyAndReplacePlacement: 'landingPages/copyAndReplacePlacement',
  getPlacedAssetUsage: 'landingPages/getPlacedAssetUsage',
  copyLandingPage: 'landingPages/copyLandingPage'
} as const

/** Ignore out-of-order `getLandingPages` responses when filters change quickly. */
let getLandingPagesRequestSeq = 0

export const getLandingPages = createAsyncThunk(
  types.getLandingPages,
  async (query: ILandingPageQuery, { rejectWithValue }) => {
    const requestSeq = ++getLandingPagesRequestSeq

    try {
      const response = await LandingPagesRepository.getLandingPages(query)

      return { ...response.data, requestSeq }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getLandingPageById = createAsyncThunk(
  types.getLandingPageById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.getLandingPageById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const listLandingPageTemplates = createAsyncThunk(
  types.listLandingPageTemplates,
  async (
    { brandId, country }: { brandId: string; country: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.listLandingPageTemplates(
        brandId,
        country
      )
      const data = response.data

      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data

      return [] as ILandingPageTemplateSummary[]
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const bootstrapLandingPageTemplate = createAsyncThunk(
  types.bootstrapLandingPageTemplate,
  async (templateId: string, { rejectWithValue }) => {
    try {
      const response =
        await LandingPagesRepository.bootstrapLandingPageTemplate(templateId)

      return response.data as ILandingPageTemplateBootstrap
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const createLandingPage = createAsyncThunk(
  types.createLandingPage,
  async (payload: ICreateLandingPageRequest, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.createLandingPage(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateLandingPage = createAsyncThunk(
  types.updateLandingPage,
  async (
    { id, payload }: { id: string; payload: IUpdateLandingPageDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.updateLandingPage(
        id,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteLandingPage = createAsyncThunk(
  types.deleteLandingPage,
  async (id: string, { rejectWithValue }) => {
    try {
      await LandingPagesRepository.deleteLandingPage(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const copyLandingPage = createAsyncThunk(
  types.copyLandingPage,
  async (
    {
      id,
      shouldCopyAssets
    }: { id: string; shouldCopyAssets?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.copyLandingPage(id, {
        shouldCopyAssets: Boolean(shouldCopyAssets)
      })

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getConfigByBrandId = createAsyncThunk(
  types.getConfigByBrandId,
  async (brandId: string, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.getConfigByBrandId(brandId)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  },
  {
    condition: (brandId, { getState }) => {
      const lp = (getState() as { landingPages: ILandingPagesState }).landingPages

      if (lp.configLoading) return false
      if (lp.configBrandId === brandId && lp.config != null) return false

      return true
    }
  }
)

export const getLandingPageFontsForBrand = createAsyncThunk(
  types.getLandingPageFontsForBrand,
  async (brandId: string, { rejectWithValue }) => {
    try {
      const response =
        await LandingPagesRepository.getLandingPageFontsForBrand(brandId)
      const data = response.data

      return Array.isArray(data) ? data : []
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  },
  {
    condition: (brandId, { getState }) => {
      const lp = (getState() as { landingPages: ILandingPagesState }).landingPages

      if (lp.brandFontsLoading) return false
      if (lp.brandFontsBrandId === brandId && lp.brandFonts != null) {
        return false
      }

      return true
    }
  }
)

export const createConfig = createAsyncThunk(
  types.createConfig,
  async (payload: ICreateConfigRequest, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.createConfig(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateConfig = createAsyncThunk(
  types.updateConfig,
  async (
    { id, payload }: { id: string; payload: IUpdateConfigDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.updateConfig(id, payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteConfig = createAsyncThunk(
  types.deleteConfig,
  async (id: string, { rejectWithValue }) => {
    try {
      await LandingPagesRepository.deleteConfig(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const addSectionTypeToConfig = createAsyncThunk(
  types.addSectionTypeToConfig,
  async (
    { configId, body }: { configId: string; body: IConfigAvailableSectionType },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.addSectionTypeToConfig(
        configId,
        body
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const removeSectionTypeFromConfig = createAsyncThunk(
  types.removeSectionTypeFromConfig,
  async (
    {
      configId,
      sectionTypeId
    }: { configId: string; sectionTypeId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.removeSectionTypeFromConfig(
        configId,
        sectionTypeId
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getLandingPageAssetTypes = createAsyncThunk(
  types.getLandingPageAssetTypes,
  async (
    query: { brandId?: string; limit?: { take: number; page: number } },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.getLandingPageAssetTypes(
        query
      )

      return {
        data: response.data?.data ?? response.data ?? [],
        meta: response.data?.meta ?? {
          recordsTotal: 0,
          pagesTotal: 0,
          take: 0,
          page: 0
        }
      }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  },
  {
    condition: (query, { getState }) => {
      const brandId = query.brandId

      if (!brandId) return true
      const lp = (getState() as { landingPages: ILandingPagesState }).landingPages

      if (
        lp.adminAssetTypesBrandId === brandId &&
        lp.adminAssetTypes.length > 0
      ) {
        return false
      }

      return true
    }
  }
)

export const createLandingPageAssetType = createAsyncThunk(
  types.createLandingPageAssetType,
  async (payload: ICreateAssetTypeRequest, { rejectWithValue }) => {
    try {
      const response =
        await LandingPagesRepository.createLandingPageAssetType(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateLandingPageAssetType = createAsyncThunk(
  types.updateLandingPageAssetType,
  async (
    {
      id,
      payload
    }: { id: string; payload: IUpdateAssetTypeDto },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await LandingPagesRepository.updateLandingPageAssetType(id, payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteLandingPageAssetType = createAsyncThunk(
  types.deleteLandingPageAssetType,
  async (id: string, { rejectWithValue }) => {
    try {
      await LandingPagesRepository.deleteLandingPageAssetType(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAvailableAssetTypes = createAsyncThunk(
  types.getAvailableAssetTypes,
  async (brandId: string, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.getAvailableAssetTypes(
        brandId
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export interface IListLandingPageAssetsQuery {
  /** When set, list is restricted to this landing page's brand, country and product categories (use this flow for "Page assets" from LP). */
  landingPageId?: string
  brandId?: string
  productCategoryId?: string
  landingPageAssetTypeId?: string
  /** Filter by subtype id, or `null` sentinel for unclassified */
  landingPageAssetSubtypeId?: string
  country?: string
  isActive?: boolean
  templateAsset?: boolean
  /** filter[name] – partial match on asset name */
  name?: string
  /** search – applied to name, defaultDescription, descriptionForEuro, descriptionForTIM, banner copy fields (if API supports) */
  search?: string
  limit?: {
    take: number
    page: number
  }
  order?: {
    by?: string
    dir?: string
  }
}

/**
 * Ignore out-of-order `listLandingPageAssets` responses when filters change quickly
 * or when LP edit-page section fetches finish after navigating to the assets table.
 */
let listLandingPageAssetsRequestSeq = 0

/** Invalidate in-flight list responses (e.g. assets table mount after edit-page storm). */
export const invalidateListLandingPageAssetsRequests = () => {
  listLandingPageAssetsRequestSeq += 1
}

export const listLandingPageAssets = createAsyncThunk(
  types.listLandingPageAssets,
  async (query: IListLandingPageAssetsQuery, { rejectWithValue }) => {
    const requestSeq = ++listLandingPageAssetsRequestSeq

    try {
      const response = await LandingPagesRepository.listLandingPageAssets(query)

      // Response structure: { data: LandingPageAsset[], meta: PaginationMeta }
      return {
        data: response.data.data,
        meta: response.data.meta,
        requestSeq
      }
    } catch (err: any) {
      return rejectWithValue({
        ...(err?.response?.data ?? {}),
        requestSeq
      })
    }
  }
)

export const getLandingPageAssetById = createAsyncThunk(
  types.getLandingPageAssetById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.getLandingPageAssetById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const createLandingPageAsset = createAsyncThunk(
  types.createLandingPageAsset,
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response =
        await LandingPagesRepository.createLandingPageAssetFromForm(formData)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateLandingPageAsset = createAsyncThunk(
  types.updateLandingPageAsset,
  async (
    { id, formData }: { id: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await LandingPagesRepository.updateLandingPageAssetFromForm(
          id,
          formData
        )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

/** Update LP asset with JSON body (e.g. Product Box: name, defaultDescription only). PATCH /landing-page-assets/:id */
export const updateLandingPageAssetWithJson = createAsyncThunk(
  types.updateLandingPageAssetWithJson,
  async (
    {
      id,
      payload
    }: {
      id: string
      payload: { name?: string; defaultDescription?: string; [key: string]: any }
    },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await LandingPagesRepository.updateLandingPageAsset(id, payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteLandingPageAsset = createAsyncThunk(
  types.deleteLandingPageAsset,
  async (id: string, { rejectWithValue }) => {
    try {
      await LandingPagesRepository.deleteLandingPageAsset(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const copyLandingPageAsset = createAsyncThunk(
  types.copyLandingPageAsset,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.copyLandingPageAsset(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const copyAndReplacePlacement = createAsyncThunk(
  types.copyAndReplacePlacement,
  async (
    {
      landingPageId,
      payload
    }: {
      landingPageId: string
      payload: {
        productCategoryId: string
        landingPageAssetId?: string
        sectionAssetId?: string
        sectionId?: string
        slot?: 'lead'
      }
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.copyAndReplacePlacement(
        landingPageId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getPlacedAssetUsage = createAsyncThunk(
  types.getPlacedAssetUsage,
  async (
    {
      landingPageId,
      assetId
    }: { landingPageId: string; assetId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.getPlacedAssetUsage(
        landingPageId,
        assetId
      )

      return response.data as { usedOnOtherLandingPages: boolean }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const copyFromProductAsset = createAsyncThunk(
  types.copyFromProductAsset,
  async (assetId: string, { rejectWithValue }) => {
    try {
      const response =
        await LandingPagesRepository.copyFromProductAsset(assetId)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const copyHotpointsFromProduct = createAsyncThunk(
  types.copyHotpointsFromProduct,
  async (productId: string, { rejectWithValue }) => {
    try {
      const response =
        await LandingPagesRepository.copyHotpointsFromProduct(productId)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAvailableAssets = createAsyncThunk(
  types.getAvailableAssets,
  async (
    {
      landingPageId,
      landingPageAssetTypeId
    }: { landingPageId: string; landingPageAssetTypeId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.getAvailableAssets(
        landingPageId,
        landingPageAssetTypeId
      )
      const raw = response.data
      const data = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : []

      return { landingPageAssetTypeId, data }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const addSection = createAsyncThunk(
  types.addSection,
  async (
    {
      landingPageId,
      payload
    }: { landingPageId: string; payload: IAddSectionRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.addSection(
        landingPageId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateSection = createAsyncThunk(
  types.updateSection,
  async (
    {
      landingPageId,
      sectionId,
      payload
    }: {
      landingPageId: string
      sectionId: string
      payload: IUpdateSectionRequest
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.updateSection(
        landingPageId,
        sectionId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteSection = createAsyncThunk(
  types.deleteSection,
  async (
    { landingPageId, sectionId }: { landingPageId: string; sectionId: string },
    { rejectWithValue }
  ) => {
    try {
      await LandingPagesRepository.deleteSection(landingPageId, sectionId)

      return sectionId
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const reorderSections = createAsyncThunk(
  types.reorderSections,
  async (
    {
      landingPageId,
      payload
    }: { landingPageId: string; payload: IReorderSectionsRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.reorderSections(
        landingPageId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const generatePackage = createAsyncThunk(
  types.generatePackage,
  async (
    {
      landingPageId,
      payload
    }: { landingPageId: string; payload: IGenerateLandingPagePackageRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.generatePackage(
        landingPageId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const generateIframe = createAsyncThunk(
  types.generateIframe,
  async (
    {
      landingPageId,
      payload
    }: { landingPageId: string; payload: IGenerateLandingPagePackageRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.generateIframe(
        landingPageId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const generatePackages = createAsyncThunk(
  types.generatePackages,
  async (payload: IGenerateLandingPagePackagesRequest, { rejectWithValue }) => {
    try {
      const response = await LandingPagesRepository.generatePackages(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const addAssetToSection = createAsyncThunk(
  types.addAssetToSection,
  async (
    {
      landingPageId,
      sectionId,
      payload
    }: {
      landingPageId: string
      sectionId: string
      payload: IAddAssetToSectionRequest
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.addAssetToSection(
        landingPageId,
        sectionId,
        payload
      )

      return { sectionId, asset: response.data }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const removeAssetFromSection = createAsyncThunk(
  types.removeAssetFromSection,
  async (
    {
      landingPageId,
      sectionId,
      assetId
    }: {
      landingPageId: string
      sectionId: string
      assetId: string
    },
    { rejectWithValue }
  ) => {
    try {
      await LandingPagesRepository.removeAssetFromSection(
        landingPageId,
        sectionId,
        assetId
      )

      return { sectionId, assetId }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getPreview = createAsyncThunk(
  types.getPreview,
  async (
    {
      landingPageId,
      partnerId,
      contentParams
    }: {
      landingPageId: string
      partnerId: string
      contentParams?: Array<{ key: string; value: string }>
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.getPreview(
        landingPageId,
        partnerId,
        contentParams
      )

      return response.data // { html: string }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getLandingPageVariantsList = createAsyncThunk(
  types.getLandingPageVariantsList,
  async (
    { brandName, partnerName }: { brandName: string; partnerName: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.getLandingPageVariantsList(
        brandName,
        partnerName
      )

      return response.data as ILandingPageVariantsListResponse
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

/** Preferred for variant picker: from config, no partner; same response shape. */
export const getLandingPageVariantsWithPreviews = createAsyncThunk(
  types.getLandingPageVariantsWithPreviews,
  async (
    {
      brandName
    }: {
      brandName: string
      /** Bypass Redux cache (after upload/delete or when previews must refresh). */
      force?: boolean
    },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await LandingPagesRepository.getLandingPageVariantsWithPreviews(
          brandName
        )

      return normalizeLandingPageVariantsListResponse(response.data)
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  },
  {
    condition: ({ brandName, force }, { getState }) => {
      if (force) return true
      const lp = (getState() as { landingPages: ILandingPagesState }).landingPages

      return !(
        lp.variantsListBrandSlug === brandName && lp.variantsList != null
      )
    }
  }
)

export const uploadVariantPreviewImage = createAsyncThunk(
  types.uploadVariantPreviewImage,
  async (
    {
      brandName,
      assetTypeName,
      variantName,
      file
    }: {
      brandName: string
      assetTypeName: string
      variantName: string
      file: File
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.uploadVariantPreviewImage(
        brandName,
        assetTypeName,
        variantName,
        file
      )

      return { ...response.data, brandName }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteVariantPreviewImage = createAsyncThunk(
  types.deleteVariantPreviewImage,
  async (
    {
      brandName,
      assetTypeName,
      variantName
    }: {
      brandName: string
      assetTypeName: string
      variantName: string
    },
    { rejectWithValue }
  ) => {
    try {
      await LandingPagesRepository.deleteVariantPreviewImage(
        brandName,
        assetTypeName,
        variantName
      )

      return { brandName }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateVariantLabel = createAsyncThunk(
  types.updateVariantLabel,
  async (
    {
      brandName,
      assetTypeName,
      variantName,
      label
    }: {
      brandName: string
      assetTypeName: string
      variantName: string
      label: string | null
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await LandingPagesRepository.updateVariantLabel(
        brandName,
        assetTypeName,
        variantName,
        label
      )

      return { ...response.data, brandName }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const landingPagesSlice = createSlice({
  name: 'landingPages',
  initialState,
  reducers: {
    clearCurrentLandingPage: (state: ILandingPagesState) => {
      state.currentLandingPage = null
    },
    setCurrentLandingPage: (state: ILandingPagesState, action: { payload: ILandingPage }) => {
      state.currentLandingPage = action.payload
    },
    clearError: (state: ILandingPagesState) => {
      state.error = null
    },
    /**
     * In-flight list counter must not survive persist/rehydrate (otherwise loading stays true forever).
     */
    resetLandingPageAssetsListPendingCount: (state: ILandingPagesState) => {
      state.landingPageAssetsListPendingCount = 0
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<ILandingPagesState>) => {
    builder
      // Get Landing Pages
      .addCase(getLandingPages.pending, state => {
        state.landingPagesListStatus = LoadingStatus.loading
        state.error = null
      })
      .addCase(getLandingPages.fulfilled, (state, action) => {
        if (action.payload.requestSeq !== getLandingPagesRequestSeq) {
          return
        }
        state.landingPagesListStatus = LoadingStatus.loaded
        state.landingPages = action.payload.data
        state.pagination = action.payload.meta
      })
      .addCase(getLandingPages.rejected, (state, action) => {
        state.landingPagesListStatus = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Get Landing Page By ID
      .addCase(getLandingPageById.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(getLandingPageById.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.currentLandingPage = action.payload
      })
      .addCase(getLandingPageById.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Create Landing Page
      .addCase(createLandingPage.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(createLandingPage.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.currentLandingPage = action.payload
      })
      .addCase(createLandingPage.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Update Landing Page
      .addCase(updateLandingPage.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(updateLandingPage.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.currentLandingPage = action.payload
        // Update in list if exists
        const index = state.landingPages.findIndex(
          lp => lp.id === action.payload.id
        )

        if (index !== -1) {
          state.landingPages[index] = action.payload
        }
      })
      .addCase(updateLandingPage.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Delete Landing Page
      .addCase(deleteLandingPage.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(deleteLandingPage.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.landingPages = state.landingPages.filter(
          lp => lp.id !== action.payload
        )
        if (state.currentLandingPage?.id === action.payload) {
          state.currentLandingPage = null
        }
      })
      .addCase(deleteLandingPage.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(copyLandingPage.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(copyLandingPage.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(copyLandingPage.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Get Config By Brand ID
      .addCase(getConfigByBrandId.pending, (state, action) => {
        state.configLoading = true
        state.error = null
        const brandId = action.meta.arg

        if (state.configBrandId !== brandId) {
          state.config = null
        }
      })
      .addCase(getConfigByBrandId.fulfilled, (state, action) => {
        state.configLoading = false
        state.config = action.payload
        state.configBrandId = action.meta.arg
      })
      .addCase(getConfigByBrandId.rejected, (state, action) => {
        state.configLoading = false
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(getLandingPageFontsForBrand.pending, (state, action) => {
        state.brandFontsLoading = true
        const brandId = action.meta.arg

        if (state.brandFontsBrandId !== brandId) {
          state.brandFonts = null
          state.brandFontsBrandId = brandId
        }
      })
      .addCase(getLandingPageFontsForBrand.fulfilled, (state, action) => {
        state.brandFontsLoading = false
        state.brandFonts = action.payload
        state.brandFontsBrandId = action.meta.arg
      })
      .addCase(getLandingPageFontsForBrand.rejected, (state, action) => {
        state.brandFontsLoading = false
        if (state.brandFontsBrandId === action.meta.arg) {
          state.brandFonts = []
        }
        handleApiErrors(action, false)
      })

      // Create / Update / Delete Config (admin)
      .addCase(createConfig.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.config = action.payload
      })
      .addCase(updateConfig.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.config = action.payload
      })
      .addCase(deleteConfig.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.config = null
      })
      .addCase(addSectionTypeToConfig.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.config = action.payload
      })
      .addCase(removeSectionTypeFromConfig.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.config = action.payload
      })

      // Admin: list asset types by brand
      .addCase(getLandingPageAssetTypes.pending, (state, action) => {
        const brandId = action.meta.arg.brandId

        if (brandId && state.adminAssetTypesBrandId !== brandId) {
          state.adminAssetTypes = []
        }
      })
      .addCase(getLandingPageAssetTypes.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.adminAssetTypes = action.payload.data ?? []
        state.adminAssetTypesMeta =
          action.payload.meta ?? state.adminAssetTypesMeta
        const brandId = action.meta.arg.brandId

        if (brandId) state.adminAssetTypesBrandId = brandId
      })

      // Admin: create / update / delete asset type
      .addCase(createLandingPageAssetType.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.adminAssetTypes = [...state.adminAssetTypes, action.payload]
      })
      .addCase(updateLandingPageAssetType.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        const idx = state.adminAssetTypes.findIndex(
          (t: ILandingPageAssetType) => t.id === action.payload.id
        )

        if (idx !== -1) state.adminAssetTypes[idx] = action.payload
      })
      .addCase(deleteLandingPageAssetType.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.adminAssetTypes = state.adminAssetTypes.filter(
          (t: ILandingPageAssetType) => t.id !== action.payload
        )
      })

      // Get Available Assets (do not set global status – picker catalogs must not
      // disable SAVE / remount the LP editor while sections are already visible)
      .addCase(getAvailableAssets.pending, state => {
        state.error = null
      })
      .addCase(getAvailableAssets.fulfilled, (state, action) => {
        state.availableAssets[action.payload.landingPageAssetTypeId] =
          action.payload.data
      })
      .addCase(getAvailableAssets.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Add Section
      .addCase(addSection.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(addSection.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        if (state.currentLandingPage) {
          if (!state.currentLandingPage.sections) {
            state.currentLandingPage.sections = []
          }
          state.currentLandingPage.sections?.push(action.payload)
        }
      })
      .addCase(addSection.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Update Section
      .addCase(updateSection.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(updateSection.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        if (state.currentLandingPage?.sections) {
          const index = state.currentLandingPage.sections.findIndex(
            s => s.id === action.payload.id
          )

          if (index !== -1) {
            state.currentLandingPage.sections[index] = action.payload
          }
        }
      })
      .addCase(updateSection.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Delete Section
      .addCase(deleteSection.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(deleteSection.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        if (state.currentLandingPage?.sections) {
          state.currentLandingPage.sections =
            state.currentLandingPage.sections.filter(
              s => s.id !== action.payload
            )
        }
      })
      .addCase(deleteSection.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Reorder Sections
      .addCase(reorderSections.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(reorderSections.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        // Refresh current landing page to get updated order
      })
      .addCase(reorderSections.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Generate Package
      .addCase(generatePackage.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(generatePackage.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(generatePackage.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Generate Packages (multiple)
      .addCase(generatePackages.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(generatePackages.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(generatePackages.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Generate Iframe
      .addCase(generateIframe.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(generateIframe.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(generateIframe.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Add Asset to Section
      .addCase(addAssetToSection.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(addAssetToSection.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        if (state.currentLandingPage?.sections) {
          const section = state.currentLandingPage.sections.find(
            s => s.id === action.payload.sectionId
          )

          if (section) {
            if (!section.sectionAssets) {
              section.sectionAssets = []
            }
            section.sectionAssets.push(action.payload.asset)
          }
        }
      })
      .addCase(addAssetToSection.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Remove Asset from Section
      .addCase(removeAssetFromSection.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(removeAssetFromSection.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        if (state.currentLandingPage?.sections) {
          const section = state.currentLandingPage.sections.find(
            s => s.id === action.payload.sectionId
          )

          if (section && section.sectionAssets) {
            section.sectionAssets = section.sectionAssets.filter(
              asset => asset.id !== action.payload.assetId
            )
          }
        }
      })
      .addCase(removeAssetFromSection.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Get Preview
      .addCase(getPreview.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(getPreview.fulfilled, state => {
        state.status = LoadingStatus.loaded
      })
      .addCase(getPreview.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Get Available Asset Types
      .addCase(getAvailableAssetTypes.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(getAvailableAssetTypes.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.availableAssetTypes = action.payload
      })
      .addCase(getAvailableAssetTypes.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // List Landing Page Assets (do not set global status – used when adding section on LP add/edit; avoids full-page loader)
      .addCase(listLandingPageAssets.pending, state => {
        // Boolean-style flag (0/1): overlapping requests must not stack a counter,
        // or loading never clears when stale responses are ignored.
        state.landingPageAssetsListPendingCount = 1
      })
      .addCase(listLandingPageAssets.fulfilled, (state, action) => {
        if (action.payload.requestSeq !== listLandingPageAssetsRequestSeq) {
          return
        }
        state.landingPageAssetsListPendingCount = 0
        state.landingPageAssets = action.payload.data
        state.landingPageAssetsPagination = action.payload.meta
      })
      .addCase(listLandingPageAssets.rejected, (state, action) => {
        const requestSeq = (action.payload as { requestSeq?: number } | undefined)
          ?.requestSeq

        if (
          requestSeq != null &&
          requestSeq !== listLandingPageAssetsRequestSeq
        ) {
          return
        }
        state.landingPageAssetsListPendingCount = 0
        handleApiErrors(action, false)
      })

      // Get Landing Page Asset By ID
      .addCase(getLandingPageAssetById.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(getLandingPageAssetById.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        // Update in list if exists
        const index = state.landingPageAssets.findIndex(
          asset => asset.id === action.payload.id
        )

        if (index !== -1) {
          state.landingPageAssets[index] = action.payload
        } else {
          state.landingPageAssets.push(action.payload)
        }
      })
      .addCase(getLandingPageAssetById.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Create Landing Page Asset
      .addCase(createLandingPageAsset.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(createLandingPageAsset.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.landingPageAssets.push(action.payload)
      })
      .addCase(createLandingPageAsset.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Update Landing Page Asset
      .addCase(updateLandingPageAsset.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(updateLandingPageAsset.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        const index = state.landingPageAssets.findIndex(
          asset => asset.id === action.payload.id
        )

        if (index !== -1) {
          state.landingPageAssets[index] = action.payload
        }
      })
      .addCase(updateLandingPageAsset.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Update Landing Page Asset (JSON)
      .addCase(updateLandingPageAssetWithJson.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(updateLandingPageAssetWithJson.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        const index = state.landingPageAssets.findIndex(
          asset => asset.id === action.payload.id
        )

        if (index !== -1) {
          state.landingPageAssets[index] = action.payload
        }
      })
      .addCase(updateLandingPageAssetWithJson.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Delete Landing Page Asset
      .addCase(deleteLandingPageAsset.pending, state => {
        state.status = LoadingStatus.loading
        state.error = null
      })
      .addCase(deleteLandingPageAsset.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.landingPageAssets = state.landingPageAssets.filter(
          asset => asset.id !== action.payload
        )
      })
      .addCase(deleteLandingPageAsset.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      // Copy (clone) Landing Page Asset
      .addCase(copyLandingPageAsset.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        if (action.payload) state.landingPageAssets.push(action.payload)
      })
      .addCase(copyLandingPageAsset.rejected, (state, action) => {
        state.status = LoadingStatus.failed
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(copyAndReplacePlacement.rejected, (state, action) => {
        handleApiErrors(action, false)
      })

      // Copy from product asset → landing page asset
      .addCase(copyFromProductAsset.rejected, (state, action) => {
        handleApiErrors(action, false)
      })

      .addCase(copyHotpointsFromProduct.rejected, (state, action) => {
        handleApiErrors(action, false)
      })

      // Get Landing Page Variants List (filesystem-based, brand+partner)
      .addCase(getLandingPageVariantsList.fulfilled, (state, action) => {
        state.variantsList = action.payload
      })
      .addCase(getLandingPageVariantsList.rejected, (state, action) => {
        state.variantsList = null
        handleApiErrors(action, false)
      })

      // Get variants with previews (config-based, brand only – preferred for variant picker)
      .addCase(getLandingPageVariantsWithPreviews.pending, (state, action) => {
        state.variantsListLoading = true
        const brandName = action.meta.arg.brandName

        if (state.variantsListBrandSlug !== brandName) {
          state.variantsList = null
          state.variantsListBrandSlug = brandName
        }
      })
      .addCase(getLandingPageVariantsWithPreviews.fulfilled, (state, action) => {
        state.variantsListLoading = false
        state.variantsList = action.payload
        state.variantsListBrandSlug = action.meta.arg.brandName
      })
      .addCase(getLandingPageVariantsWithPreviews.rejected, (state, action) => {
        state.variantsListLoading = false
        if (state.variantsListBrandSlug === action.meta.arg.brandName) {
          state.variantsList = null
        }
        handleApiErrors(action, false)
      })

      .addCase(uploadVariantPreviewImage.fulfilled, (state, action) => {
        const { assetTypeName, variantName } = action.meta.arg
        const payload = action.payload as {
          imageUrl?: string
          previewImageUrl?: string
        }
        const url =
          (typeof payload?.imageUrl === 'string' && payload.imageUrl.trim()) ||
          (typeof payload?.previewImageUrl === 'string' &&
            payload.previewImageUrl.trim()) ||
          null

        if (!url || !state.variantsList?.assetTypes) return
        for (const at of state.variantsList.assetTypes) {
          if (at.assetTypeName !== assetTypeName) continue
          const variant = at.variants?.find(v => v.name === variantName)

          if (variant) variant.previewImageUrl = url
        }
      })
      .addCase(uploadVariantPreviewImage.rejected, (state, action) => {
        handleApiErrors(action, false)
      })
      .addCase(deleteVariantPreviewImage.fulfilled, (state, action) => {
        const { assetTypeName, variantName } = action.meta.arg

        if (!state.variantsList?.assetTypes) return
        for (const at of state.variantsList.assetTypes) {
          if (at.assetTypeName !== assetTypeName) continue
          const variant = at.variants?.find(v => v.name === variantName)

          if (variant) variant.previewImageUrl = null
        }
      })
      .addCase(deleteVariantPreviewImage.rejected, (state, action) => {
        handleApiErrors(action, false)
      })
      .addCase(updateVariantLabel.rejected, (state, action) => {
        handleApiErrors(action, false)
      })
  }
})

export const {
  clearCurrentLandingPage,
  setCurrentLandingPage,
  clearError,
  resetLandingPageAssetsListPendingCount
} = landingPagesSlice.actions

export const selectLandingPages = (state: any) => state.landingPages

export const selectLandingPagesList = createSelector(
  selectLandingPages,
  state => state.landingPages
)

export const selectCurrentLandingPage = createSelector(
  selectLandingPages,
  state => state.currentLandingPage
)

export const selectLandingPagesStatus = createSelector(
  selectLandingPages,
  state => state.status
)

export const selectLandingPagesListStatus = createSelector(
  selectLandingPages,
  state => state.landingPagesListStatus ?? LoadingStatus.initial
)

export const selectLandingPagesPagination = createSelector(
  selectLandingPages,
  state => state.pagination
)

export const selectLandingPagesConfig = createSelector(
  selectLandingPages,
  state => state.config
)

export const selectAdminAssetTypes = createSelector(
  selectLandingPages,
  state => state.adminAssetTypes ?? []
)

export const selectAdminAssetTypesMeta = createSelector(
  selectLandingPages,
  state => state.adminAssetTypesMeta ?? { page: 0, pagesTotal: 0, recordsTotal: 0, take: 0 }
)

export const selectVariantsList = createSelector(
  selectLandingPages,
  state => state.variantsList
)

export const selectAvailableAssets = createSelector(
  selectLandingPages,
  state => state.availableAssets
)

export const selectLandingPagesError = createSelector(
  selectLandingPages,
  state => state.error
)

export const selectAvailableAssetTypes = createSelector(
  selectLandingPages,
  state => state.availableAssetTypes
)

export const selectLandingPageAssets = createSelector(
  selectLandingPages,
  state => state.landingPageAssets || []
)

export const selectLandingPageAssetsPagination = createSelector(
  selectLandingPages,
  state =>
    state.landingPageAssetsPagination || {
      page: 0,
      pagesTotal: 0,
      recordsTotal: 0,
      take: 0
    }
)

export const selectLandingPageAssetsListLoading = createSelector(
  selectLandingPages,
  state => (state.landingPageAssetsListPendingCount ?? 0) > 0
)

export const selectLandingPagesConfigLoading = createSelector(
  selectLandingPages,
  state => state.configLoading ?? false
)

export const selectVariantsListLoading = createSelector(
  selectLandingPages,
  state => state.variantsListLoading ?? false
)

export const selectLandingPageBrandFonts = (brandId: string) =>
  createSelector(selectLandingPages, state =>
    state.brandFontsBrandId === brandId ? state.brandFonts ?? [] : []
  )

export const selectLandingPageBrandFontsLoading = (brandId: string) =>
  createSelector(selectLandingPages, state => {
    if (!brandId) return false
    if (
      state.brandFontsBrandId === brandId &&
      state.brandFonts != null
    ) {
      return false
    }

    return (
      state.brandFontsBrandId !== brandId || Boolean(state.brandFontsLoading)
    )
  })

export const selectLandingPageAssetById = (id: string) =>
  createSelector(selectLandingPages, state =>
    state.landingPageAssets.find((asset: ILandingPageAsset) => asset.id === id)
  )

export default landingPagesSlice.reducer
